import { useState } from 'react';
import { Modal } from '@/shared/ui/Modal';
import { Button } from '@/shared/ui/Button';
import { useAuth } from '@/app/providers/AuthProvider';
import { useToast } from '@/app/providers/ToastProvider';
import { useScopedNavigate } from '@/app/providers/TenantScopeProvider';
import { api, ApiError } from '@/shared/api/client';
import {
  PLANS,
  featureLabel,
  normalizePlan,
  type PlanDef,
  type PlanId,
} from '@/shared/format/plan';

interface Props {
  open: boolean;
  onClose: () => void;
}

type Period = 'monthly' | 'annual';

function priceBadge(p: PlanDef, period: Period): string {
  if (!p.priceMonthly) return 'Үнэгүй'; // 0 (Free Trial)
  if (period === 'annual' && p.priceAnnual) {
    return p.priceAnnual.toLocaleString('mn-MN') + '₮/жил';
  }
  return p.priceMonthly.toLocaleString('mn-MN') + '₮/сар';
}

function annualSaving(p: PlanDef): string | null {
  if (!p.priceMonthly || !p.priceAnnual) return null;
  const saved = p.priceMonthly * 12 - p.priceAnnual;
  if (saved <= 0) return null;
  return saved.toLocaleString('mn-MN') + '₮ хэмнэх';
}

export function PlanModal({ open, onClose }: Props) {
  const { snapshot } = useAuth();
  const { toast } = useToast();
  const navScoped = useScopedNavigate();
  const [period, setPeriod] = useState<Period>('monthly');
  const [busyId, setBusyId] = useState<PlanId | null>(null);
  const current = normalizePlan(snapshot.plan);

  async function pick(p: PlanDef) {
    if (!p.priceMonthly) {
      // Free Trial (0₮) — нэхэмжлэх үүсгэхгүй.
      toast('Free Trial аль хэдийн идэвхтэй.', 'info');
      return;
    }
    if (!snapshot.tenantId) {
      toast('Тенант сонгогдоогүй байна', 'warning');
      return;
    }
    const periodMonths = period === 'annual' ? 12 : 1;
    setBusyId(p.id);
    try {
      const inv = await api.post<{ Id: string }>('/api/invoice', {
        TenantId: snapshot.tenantId,
        Plan: p.serverId,
        PeriodMonths: periodMonths,
      });
      onClose();
      navScoped('invoiceDetail', { id: inv.Id });
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Нэхэмжлэх үүсгэхэд алдаа гарлаа';
      toast(msg, 'danger');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title={
        <span>
          <i className="bi bi-layers" style={{ color: 'var(--ts-brand)', marginRight: 8 }} />
          Багц сонгох
        </span>
      }
    >
      {/* Period selector */}
      <div style={{ marginBottom: 22, display: 'flex', justifyContent: 'center' }}>
        <div
          style={{
            display: 'inline-flex',
            background: 'rgba(128,128,128,0.12)',
            borderRadius: 999,
            padding: 4,
            gap: 2,
          }}
        >
          <button
            type="button"
            onClick={() => setPeriod('monthly')}
            style={{
              border: 0,
              padding: '7px 18px',
              borderRadius: 999,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.82rem',
              background: period === 'monthly' ? 'var(--ts-card-bg)' : 'transparent',
              color: 'var(--bs-body-color)',
              boxShadow: period === 'monthly' ? '0 2px 6px rgba(0,0,0,0.18)' : 'none',
            }}
          >
            Сар бүр
          </button>
          <button
            type="button"
            onClick={() => setPeriod('annual')}
            style={{
              border: 0,
              padding: '7px 18px',
              borderRadius: 999,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.82rem',
              background: period === 'annual' ? 'var(--ts-card-bg)' : 'transparent',
              color: 'var(--bs-body-color)',
              boxShadow: period === 'annual' ? '0 2px 6px rgba(0,0,0,0.18)' : 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            Жилээр
            <span
              style={{
                background: 'rgba(34,197,94,0.15)',
                color: '#22c55e',
                fontSize: '0.62rem',
                fontWeight: 700,
                padding: '2px 7px',
                borderRadius: 999,
              }}
            >
              17% хэмнэх
            </span>
          </button>
        </div>
      </div>

      {/* Plan cards — 3 ширхэг 1 мөрөнд (Free/Starter/Gold). */}
      <div className="ts-plan-grid">
        {PLANS.map((p) => {
          const isCurrent = p.id === current;
          const popular = p.id === 'gold';
          const saving = period === 'annual' ? annualSaving(p) : null;
          return (
            <div
              key={p.id}
              className="ts-card"
              style={{
                position: 'relative',
                padding: '18px 16px 16px',
                display: 'flex',
                flexDirection: 'column',
                borderColor: popular || isCurrent ? p.color : 'var(--ts-border)',
                boxShadow: popular
                  ? `0 0 0 1px ${p.color} inset, 0 8px 22px rgba(0,0,0,0.18)`
                  : undefined,
                opacity: isCurrent ? 0.85 : 1,
              }}
            >
              {popular ? (
                <div
                  style={{
                    position: 'absolute',
                    top: -10,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: p.color,
                    color: '#fff',
                    fontSize: '0.62rem',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    padding: '3px 10px',
                    borderRadius: 999,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Хамгийн алдартай
                </div>
              ) : null}

              <div
                style={{
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  color: p.color,
                  marginBottom: 4,
                }}
              >
                {p.label}
              </div>
              <div
                style={{
                  fontSize: '1.35rem',
                  fontWeight: 800,
                  lineHeight: 1.2,
                  color: 'var(--bs-body-color)',
                  margin: '6px 0 2px',
                }}
              >
                {priceBadge(p, period)}
              </div>
              {saving ? (
                <div
                  style={{
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: '#22c55e',
                    marginBottom: 6,
                  }}
                >
                  {saving}
                </div>
              ) : null}
              <div
                style={{
                  fontSize: '0.75rem',
                  color: 'var(--ts-muted)',
                  marginBottom: 12,
                  minHeight: 32,
                  lineHeight: 1.4,
                }}
              >
                {p.description}
              </div>

              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: '0 0 14px',
                  fontSize: '0.75rem',
                  lineHeight: 1.6,
                  color: 'var(--bs-body-color)',
                  flex: 1,
                }}
              >
                {p.features.map((f) => (
                  <li key={f} style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <i
                      className="bi bi-check-lg"
                      style={{ color: p.color, marginRight: 6, flexShrink: 0 }}
                    />
                    {featureLabel(f)}
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => pick(p)}
                disabled={isCurrent || busyId === p.id}
                style={{
                  width: '100%',
                  background: isCurrent ? 'rgba(128,128,128,0.25)' : p.color,
                  color: isCurrent ? 'var(--ts-muted)' : '#fff',
                }}
              >
                {isCurrent ? (
                  <>
                    <i className="bi bi-check-circle" /> Идэвхтэй
                  </>
                ) : busyId === p.id ? (
                  'Үүсгэж байна…'
                ) : (
                  'Сонгох'
                )}
              </Button>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
